import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import apiService from "../Services/api.service";
import CustomButton from "./Button";
import { useToast } from "./Toast";
const INSTALLMENT_RULES = {
  monthly: { count: 12, extraPercent: 15 },
  quarterly: { count: 4, extraPercent: 10 },
  "bi-annual": { count: 2, extraPercent: 5 },
};

const calculateInstallmentPricing = (basePrice, installmentType, waiveSurcharge = false) => {
  const rule = INSTALLMENT_RULES[installmentType];
  if (!rule) throw new Error("Invalid installment type");

  const extraPercent = waiveSurcharge ? 0 : rule.extraPercent;
  const surcharge = (basePrice * extraPercent) / 100;
  const finalAmount = basePrice + surcharge; // ❗ NO ROUNDING

  return {
    baseAmount: basePrice,
    finalAmount, // may be decimal
    installmentCount: rule.count,
    extraPercent,
  };
};
export default function ActionSubscriptionModal({
  open,
  onClose,
  token,
  doctorId,
  clinicDetails = [],
  plans = [],
  mode = "create", // "create" | "renew"
  subscriptionId,
  baseRenewPrice,
  renewDefaultStartDate,
}) {
  const Toast = useToast();

  // ⭐ Extract all unique regions from all plan.geoPricing
  const allRegions = Array.from(
    new Set(plans.flatMap((p) => p.geoPricing.map((g) => g.geo)))
  );

  // fallback
  const defaultRegion = allRegions[0] || "";

  /** CREATE FORM */
  const [form, setForm] = useState({
    doctorId,
    clinics: [],
    planId: "",
    region: defaultRegion,
    startDate: "",
    endDate: "",
    customPrice: "",
    type: "standard",
    payment: {
      gateway: "manual",
      isInstallment: false,
      installmentType: "",
      waiveSurcharge: false,
      metadata: { note: "" },
    },
  });

  /** RENEW FORM */
  const [renewForm, setRenewForm] = useState({
    subscriptionId,
    startDate: "",
    endDate: "",
    finalPrice: "",
    payment: {
      gateway: "manual",
      isInstallment: false,
      installmentType: "",
      waiveSurcharge: false,
      metadata: { note: "" },
    },
  });
  // Renewing onto a different plan can take effect at two different times:
  // "now" upgrades the CURRENT still-active subscription immediately (via the
  // same upgrade-only changePlan call as the standalone Upgrade Plan action),
  // then renews normally so the next period just carries that plan forward;
  // "later" leaves the current period untouched and only switches the plan
  // on the new chained period once this one actually ends (renewSubscription
  // prices the new period off the new plan directly, no proration needed).
  const [changePlanOnRenew, setChangePlanOnRenew] = useState(false);
  const [renewNewPlanId, setRenewNewPlanId] = useState("");
  const [renewPlanChangeReason, setRenewPlanChangeReason] = useState("");
  const [renewPlanChangeTiming, setRenewPlanChangeTiming] = useState("later");
  const [installmentPreview, setInstallmentPreview] = useState(null);
  const [renewInstallmentPreview, setRenewInstallmentPreview] = useState(null);

  useEffect(() => {
    // reset region when plans change
    setForm((prev) => ({ ...prev, region: defaultRegion }));
  }, [plans]);

  /** Auto-select clinic */
  useEffect(() => {
    if (mode === "create" && clinicDetails.length === 1) {
      setForm((prev) => ({ ...prev, clinics: [clinicDetails[0]._id] }));
    }
  }, [clinicDetails, mode]);

  useEffect(() => {
    if (mode === "renew" && subscriptionId) {
      setRenewForm((prev) => ({ ...prev, subscriptionId }));
    }
  }, [subscriptionId, mode]);

  // Default the renewal's start date to the current plan's endDate — the
  // next cycle should start exactly when the current one ends, not on
  // whatever date ops happens to type. Still editable for a genuinely
  // backdated renewal. This only re-fires when a new renew action opens
  // the modal (subscriptionId/renewDefaultStartDate change together), so
  // it's safe to overwrite rather than guard against in-progress edits.
  useEffect(() => {
    if (mode === "renew") {
      const formatted = renewDefaultStartDate
        ? new Date(renewDefaultStartDate).toISOString().slice(0, 10)
        : "";
      setRenewForm((prev) => ({ ...prev, startDate: formatted }));
    }
  }, [mode, subscriptionId, renewDefaultStartDate]);

  // Final price defaults to the subscription's previous price, but stays
  // editable — ops can override it for this renewal same as any other
  // negotiated/custom price.
  useEffect(() => {
    if (mode === "renew") {
      setRenewForm((prev) => ({
        ...prev,
        finalPrice: baseRenewPrice != null ? String(baseRenewPrice) : "",
      }));
    }
  }, [mode, subscriptionId, baseRenewPrice]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleRenewChange = (key, value) => {
    setRenewForm((prev) => ({ ...prev, [key]: value }));
  };

  /** When region changes → reset plan + price */
  const handleRegionChange = (region) => {
    setForm((prev) => ({
      ...prev,
      region,
      planId: "",
      customPrice: "",
    }));
  };

  /** When plan changes → autofill price */
  const handlePlanSelect = (planId) => {
    const selectedPlan = plans.find((p) => p._id === planId);

    const regionPrice =
      selectedPlan.geoPricing.find((g) => g.geo === form.region) ||
      selectedPlan.geoPricing[0];

    handleChange("planId", planId);
  };
  const resolveBaseAmount = () => {
    // 1️⃣ Custom price always wins
    if (form.customPrice && Number(form.customPrice) > 0) {
      return Number(form.customPrice);
    }

    // 2️⃣ Fallback to plan regional price
    const selectedPlan = plans.find((p) => p._id === form.planId);
    if (!selectedPlan) return null;

    const regionPrice =
      selectedPlan.geoPricing.find((g) => g.geo === form.region) ||
      selectedPlan.geoPricing[0];

    return regionPrice?.price ?? null;
  };
  const resolveRenewBaseAmount = () => {
    // 🔹 OPTION 1: backend sends subscription price (BEST)
    // Example: renewForm.baseAmount (if you add it later)

    if (baseRenewPrice && Number(baseRenewPrice) > 0) {
      return Number(baseRenewPrice);
    }

    // 🔹 OPTION 2: fallback (if backend recalculates)
    return null;
  };

  useEffect(() => {
    if (!form.payment.isInstallment || !form.payment.installmentType) {
      setInstallmentPreview(null);
      return;
    }

    try {
      const baseAmount = resolveBaseAmount();
      if (!baseAmount) return;

      const preview = calculateInstallmentPricing(
        baseAmount,
        form.payment.installmentType,
        form.payment.waiveSurcharge
      );

      setInstallmentPreview(preview);
    } catch {
      setInstallmentPreview(null);
    }
  }, [
    form.customPrice,
    form.planId,
    form.region,
    form.payment.isInstallment,
    form.payment.installmentType,
    form.payment.waiveSurcharge,
  ]);

  useEffect(() => {
    if (
      !renewForm.payment.isInstallment ||
      !renewForm.payment.installmentType
    ) {
      setRenewInstallmentPreview(null);
      return;
    }

    try {
      const baseAmount = resolveRenewBaseAmount();
      if (!baseAmount) return;

      const preview = calculateInstallmentPricing(
        baseAmount,
        renewForm.payment.installmentType,
        renewForm.payment.waiveSurcharge
      );

      setRenewInstallmentPreview(preview);
    } catch {
      setRenewInstallmentPreview(null);
    }
  }, [
    renewForm.payment.isInstallment,
    renewForm.payment.installmentType,
    renewForm.payment.waiveSurcharge,
    renewForm.baseAmount, // if you add it
  ]);

  /** CREATE API */
  const handleCreate = async () => {
    try {
      const payload = {
        ...form,

        // ✅ DO NOT auto-convert DATE-ONLY using new Date()
        startDate: form.startDate.includes("T")
          ? new Date(form.startDate).toISOString() // datetime with timezone
          : form.startDate, // date-only → backend handles safely

        customPrice: form.customPrice ? Number(form.customPrice) : null,

        payment: {
          ...form.payment,
          installmentCount: Number(form.payment.installmentCount),
        },
      };

      if (!form.endDate) {
        delete payload.endDate;
      } else {
        payload.endDate = form.endDate.includes("T")
          ? new Date(form.endDate).toISOString()
          : form.endDate;
      }

      await apiService.createSubscription(token, payload);
      onClose();
    } catch (error) {
      Toast.error(
        error.response?.data?.message || "Error creating subscription"
      );
    }
  };

  // "Right now" only makes sense for an upgrade (it goes through the same
  // upgrade-only Change Plan path) — a downgrade can only ever land on the
  // upcoming/renewed period, so the timing choice is moot and shouldn't even
  // be offered.
  const selectedRenewPlan = plans.find((p) => p._id === renewNewPlanId);
  const selectedRenewPlanPrice = selectedRenewPlan
    ? (selectedRenewPlan.geoPricing.find((g) => g.geo === form.region) || selectedRenewPlan.geoPricing[0])
        ?.price
    : null;
  const isRenewDowngrade =
    baseRenewPrice != null &&
    selectedRenewPlanPrice != null &&
    Number(selectedRenewPlanPrice) < Number(baseRenewPrice);

  useEffect(() => {
    if (isRenewDowngrade && renewPlanChangeTiming !== "later") {
      setRenewPlanChangeTiming("later");
    }
  }, [isRenewDowngrade, renewPlanChangeTiming]);

  // Final Price follows whichever plan is actually selected — the new
  // plan's own price once one's picked (upgrade or downgrade alike), back
  // to the current plan's price if the switch is undone.
  useEffect(() => {
    if (!changePlanOnRenew || !renewNewPlanId || selectedRenewPlanPrice == null) {
      return;
    }
    setRenewForm((prev) => ({
      ...prev,
      finalPrice: String(selectedRenewPlanPrice),
    }));
  }, [changePlanOnRenew, renewNewPlanId, selectedRenewPlanPrice]);

  /** RENEW API */
  const handleRenew = async () => {
    try {
      const payload = {
        ...renewForm,

        // ✅ SAFE DATE HANDLING (same as create)
        startDate: renewForm.startDate.includes("T")
          ? new Date(renewForm.startDate).toISOString() // datetime with timezone
          : renewForm.startDate, // date-only → let backend handle

        payment: {
          ...renewForm.payment,
          installmentCount: Number(renewForm.payment.installmentCount || 0),
        },
      };

      if (!renewForm.endDate) {
        delete payload.endDate;
      } else {
        payload.endDate = renewForm.endDate.includes("T")
          ? new Date(renewForm.endDate).toISOString()
          : renewForm.endDate;
      }

      delete payload.finalPrice;
      if (renewForm.finalPrice !== "" && renewForm.finalPrice != null) {
        payload.customPrice = Number(renewForm.finalPrice);
      }

      if (changePlanOnRenew && renewNewPlanId) {
        payload.newPlanId = renewNewPlanId;
        payload.reason = renewPlanChangeReason;
        if (renewPlanChangeTiming === "now") {
          // Updates THIS subscription in place — new plan, extended end
          // date — instead of also creating a separate upcoming/next-period
          // document. Backend refuses a downgrade here (upgrade-only, same
          // as the standalone Upgrade Plan action).
          payload.inPlace = true;
        }
        // "later" (no inPlace): priced as part of the renewal for the fresh
        // next period, creating the usual chained upcoming subscription.
      }

      await apiService.renewSubscription(token, payload);

      onClose();
    } catch (error) {
      Toast.error(
        error.response?.data?.message || "Error renewing subscription"
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 20,
          pb: 1.5,
          borderBottom: "1px solid #eee",
        }}
      >
        {mode === "renew" ? "Renew Subscription" : "Create Subscription"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          {/* CREATE MODE */}
          {mode === "create" && (
            <>
              {/* ⭐ REGION SELECTOR (DYNAMIC) */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Select Region"
                  value={form.region}
                  onChange={(e) => handleRegionChange(e.target.value)}
                >
                  {allRegions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {clinicDetails.length > 1 && (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Select Clinics"
                    SelectProps={{ multiple: true }}
                    value={form.clinics}
                    onChange={(e) => handleChange("clinics", e.target.value)}
                  >
                    {clinicDetails.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* ⭐ PLAN SELECTOR with region-based pricing */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label={`Select Plan (Region: ${form.region})`}
                  value={form.planId}
                  onChange={(e) => handlePlanSelect(e.target.value)}
                >
                  {plans.map((p) => {
                    const regionPrice =
                      p.geoPricing.find((g) => g.geo === form.region) ||
                      p.geoPricing[0];

                    return (
                      <MenuItem key={p._id} value={p._id}>
                        {p.name} • {p.billingCycle} • {regionPrice.currency}{" "}
                        {regionPrice.price}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              {/* Dates */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Start Date"
                  fullWidth
                  value={form.startDate}
                  onChange={(e) => handleChange("startDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="End Date"
                  fullWidth
                  value={form.endDate}
                  onChange={(e) => handleChange("endDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Custom Price */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Custom Price"
                  fullWidth
                  value={form.customPrice}
                  onChange={(e) => handleChange("customPrice", e.target.value)}
                />
              </Grid>

              {/* Subscription Type */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Subscription Type"
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                </TextField>
              </Grid>

              {/* Installments */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Is Installment?"
                  value={form.payment.isInstallment}
                  onChange={(e) =>
                    handleChange("payment", {
                      ...form.payment,
                      isInstallment: e.target.value === "true",
                    })
                  }
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>

              {form.payment.isInstallment && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Installment Type"
                    fullWidth
                    value={form.payment.installmentType || ""}
                    onChange={(e) => {
                      const installmentType = e.target.value;

                      let preview = null;
                      try {
                        const baseAmount = resolveBaseAmount();

                        if (baseAmount && installmentType) {
                          preview = calculateInstallmentPricing(
                            baseAmount,
                            installmentType,
                            form.payment.waiveSurcharge
                          );
                        }
                      } catch (err) {
                        preview = null;
                      }

                      setInstallmentPreview(preview);

                      handleChange("payment", {
                        ...form.payment,
                        installmentType,
                      });
                    }}
                    helperText={
                      form.payment.installmentType === "monthly"
                        ? `12 installments${form.payment.waiveSurcharge ? "" : " (+15%)"}`
                        : form.payment.installmentType === "quarterly"
                        ? `4 installments${form.payment.waiveSurcharge ? "" : " (+10%)"}`
                        : form.payment.installmentType === "bi-annual"
                        ? `2 installments${form.payment.waiveSurcharge ? "" : " (+5%)"}`
                        : "Select installment type"
                    }
                  >
                    <MenuItem value="monthly">Monthly (12 payments)</MenuItem>
                    <MenuItem value="quarterly">
                      Quarterly (4 payments)
                    </MenuItem>
                    <MenuItem value="bi-annual">
                      Bi-Annual (2 payments)
                    </MenuItem>
                  </TextField>
                </Grid>
              )}

              {form.payment.isInstallment && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Add Extra Charge?"
                    value={form.payment.waiveSurcharge ? "true" : "false"}
                    onChange={(e) =>
                      handleChange("payment", {
                        ...form.payment,
                        waiveSurcharge: e.target.value === "true",
                      })
                    }
                  >
                    <MenuItem value="false">Yes, add extra charge</MenuItem>
                    <MenuItem value="true">No extra charge</MenuItem>
                  </TextField>
                </Grid>
              )}
              {form.payment.isInstallment && installmentPreview && (
                <Grid item xs={12}>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: "#F9FAFB",
                    }}
                  >
                    <Grid item xs={6}>
                      <strong>Base Amount</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      {installmentPreview.baseAmount}
                    </Grid>

                    <Grid item xs={6}>
                      <strong>Extra Charge</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      {installmentPreview.extraPercent}%
                    </Grid>

                    <Grid item xs={6}>
                      <strong>Total Amount</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right" fontWeight="bold">
                      {installmentPreview.finalAmount}
                    </Grid>

                    <Grid item xs={6}>
                      <strong>Installments</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      {installmentPreview.installmentCount}
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Note */}
              <Grid item xs={12}>
                <TextField
                  label="Note"
                  fullWidth
                  value={form.payment.metadata.note}
                  onChange={(e) =>
                    handleChange("payment", {
                      ...form.payment,
                      metadata: { note: e.target.value },
                    })
                  }
                />
              </Grid>
            </>
          )}

          {/* RENEW MODE */}
          {mode === "renew" && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="New Start Date"
                  fullWidth
                  value={renewForm.startDate}
                  onChange={(e) =>
                    handleRenewChange("startDate", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="New End Date"
                  fullWidth
                  value={renewForm.endDate}
                  onChange={(e) => handleRenewChange("endDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Defaults to the subscription's previous price, editable —
              whatever's here is what actually gets charged for this renewal. */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Final Price"
                  fullWidth
                  value={renewForm.finalPrice}
                  onChange={(e) => handleRenewChange("finalPrice", e.target.value)}
                  inputProps={{ min: 0 }}
                  onWheel={(e) => e.target.blur()}
                />
              </Grid>

              {/* Change plan while renewing — priced directly off the new
              plan for the fresh period (no proration, 0 days used either
              way), so this no longer depends on whether the renewed period
              lands in the past or future. Available for every renewal. */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Change Plan on Renew?"
                  value={changePlanOnRenew ? "true" : "false"}
                  onChange={(e) => {
                    const value = e.target.value === "true";
                    setChangePlanOnRenew(value);
                    if (!value) {
                      setRenewNewPlanId("");
                      setRenewPlanChangeReason("");
                      setRenewPlanChangeTiming("later");
                      setRenewForm((prev) => ({
                        ...prev,
                        finalPrice:
                          baseRenewPrice != null ? String(baseRenewPrice) : "",
                      }));
                    }
                  }}
                >
                  <MenuItem value="false">No, keep current plan</MenuItem>
                  <MenuItem value="true">Yes, switch plan too</MenuItem>
                </TextField>
              </Grid>

              {changePlanOnRenew && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="New Plan"
                      value={renewNewPlanId}
                      onChange={(e) => setRenewNewPlanId(e.target.value)}
                    >
                      {plans.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.name} • {p.billingCycle}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {renewNewPlanId && !isRenewDowngrade && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="When should this apply?"
                        value={renewPlanChangeTiming}
                        onChange={(e) => setRenewPlanChangeTiming(e.target.value)}
                      >
                        <MenuItem value="now">Right now</MenuItem>
                        <MenuItem value="later">After this period ends</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      label="Reason for Plan Change"
                      fullWidth
                      value={renewPlanChangeReason}
                      onChange={(e) => setRenewPlanChangeReason(e.target.value)}
                    />
                  </Grid>
                </>
              )}

              {/* Installments */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Is Installment?"
                  value={renewForm.payment.isInstallment}
                  onChange={(e) =>
                    handleRenewChange("payment", {
                      ...renewForm.payment,
                      isInstallment: e.target.value === "true",
                    })
                  }
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>

              {renewForm.payment.isInstallment && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Installment Type"
                    fullWidth
                    value={renewForm.payment.installmentType || ""}
                    onChange={(e) => {
                      const installmentType = e.target.value;

                      let preview = null;
                      try {
                        const baseAmount = resolveRenewBaseAmount();
                        if (baseAmount && installmentType) {
                          preview = calculateInstallmentPricing(
                            baseAmount,
                            installmentType,
                            renewForm.payment.waiveSurcharge
                          );
                        }
                      } catch {
                        preview = null;
                      }

                      setRenewInstallmentPreview(preview);

                      handleRenewChange("payment", {
                        ...renewForm.payment,
                        installmentType,
                      });
                    }}
                    helperText={
                      renewForm.payment.installmentType === "monthly"
                        ? `12 installments${renewForm.payment.waiveSurcharge ? "" : " (+15%)"}`
                        : renewForm.payment.installmentType === "quarterly"
                        ? `4 installments${renewForm.payment.waiveSurcharge ? "" : " (+10%)"}`
                        : renewForm.payment.installmentType === "bi-annual"
                        ? `2 installments${renewForm.payment.waiveSurcharge ? "" : " (+5%)"}`
                        : "Select installment type"
                    }
                  >
                    <MenuItem value="monthly">Monthly (12 payments)</MenuItem>
                    <MenuItem value="quarterly">
                      Quarterly (4 payments)
                    </MenuItem>
                    <MenuItem value="bi-annual">
                      Bi-Annual (2 payments)
                    </MenuItem>
                  </TextField>
                </Grid>
              )}

              {renewForm.payment.isInstallment && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Add Extra Charge?"
                    value={renewForm.payment.waiveSurcharge ? "true" : "false"}
                    onChange={(e) =>
                      handleRenewChange("payment", {
                        ...renewForm.payment,
                        waiveSurcharge: e.target.value === "true",
                      })
                    }
                  >
                    <MenuItem value="false">Yes, add extra charge</MenuItem>
                    <MenuItem value="true">No extra charge</MenuItem>
                  </TextField>
                </Grid>
              )}
              {renewForm.payment.isInstallment && renewInstallmentPreview && (
                <Grid item xs={12}>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: "#F9FAFB",
                    }}
                  >
                    <Grid item xs={6}>
                      <strong>Base Amount</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      {renewInstallmentPreview.baseAmount}
                    </Grid>

                    <Grid item xs={6}>
                      <strong>Extra Charge</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      {renewInstallmentPreview.extraPercent}%
                    </Grid>

                    <Grid item xs={6}>
                      <strong>Total Amount</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right" fontWeight="bold">
                      {renewInstallmentPreview.finalAmount}
                    </Grid>

                    <Grid item xs={6}>
                      <strong>Installments</strong>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      {renewInstallmentPreview.installmentCount}
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Note */}
              <Grid item xs={12}>
                <TextField
                  label="Note"
                  fullWidth
                  value={renewForm.payment.metadata.note}
                  onChange={(e) =>
                    handleRenewChange("payment", {
                      ...renewForm.payment,
                      metadata: { note: e.target.value },
                    })
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: "1px solid #eee" }}>
        <CustomButton
          text="Cancel"
          variant="outlined"
          onClick={onClose}
          width="120px"
        />

        {mode === "create" ? (
          <CustomButton
            text="Create"
            onClick={handleCreate}
            width="150px"
            disabled={!form.planId || !form.startDate}
          />
        ) : (
          <CustomButton
            text="Renew"
            onClick={handleRenew}
            width="150px"
            disabled={
              !renewForm.startDate ||
              (changePlanOnRenew && !renewNewPlanId)
            }
          />
        )}
      </DialogActions>
    </Dialog>
  );
}
